import {
  Box,
  Button,
  Collapse,
  type ColorProps,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import {
  faCalendar,
  faCertificate,
  faCircleInfo,
  faClock,
  faGlobe,
  faStar,
  faThumbsUp,
  faTrophy,
  faUserPlus,
  faUsers,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'

import { countryCodeToName } from '@/utils/helpers'

import UserEventQ from '@/queries/UserEventQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'
import { PROGRESSION_DEFAULT } from '@/client/services/progression'
import { TUTORING_MINUTES_TOOLTIP_EXPLANATION } from '@/client/services/user'

import Icon from '@/components/UI/Icon/Icon'
import Tippy from '@/components/UI/Tippy'

import ProfileItemContainer from './ProfileItemContainer'

type Stat = {
  icon: IconDefinition
  label: string
  value: number | string
  color?: ColorProps['color']
  tooltipText?: string
  infoTooltipText?: string
}

const SHOW_ALL_LIMIT = 6

function StatsList({ stats }: { stats: Stat[] }): React.ReactElement {
  return (
    <SimpleGrid columns={2} spacingY={6} spacingX={5}>
      {stats.map(
        ({ value, label, icon, color = null, tooltipText = null, infoTooltipText = null }) => (
          <Stack direction="row" align="center" key={label}>
            <Box minW="24px" color={color ?? 'fg.muted'}>
              <FontAwesomeIcon icon={icon} fontSize="24px" />
            </Box>
            <Tippy content={tooltipText} disabled={tooltipText == null}>
              <Box>
                <Stack direction="row" spacing="4px">
                  <Text fontWeight={600} fontSize="16px">
                    {value}
                  </Text>
                  {infoTooltipText != null ? (
                    <Tippy content={infoTooltipText}>
                      <Box>
                        <Icon faIcon={faCircleInfo} size="sm" color="fg.muted" />
                      </Box>
                    </Tippy>
                  ) : null}
                </Stack>
                <Text color="fg.muted" fontSize="14px" lineHeight="18px">
                  {label}
                </Text>
              </Box>
            </Tippy>
          </Stack>
        )
      )}
    </SimpleGrid>
  )
}

export default function ProfileStats({ user }: { user: Query }): JSX.Element {
  const currentUser = useUser()
  const [showAll, setShowAll] = React.useState(false)

  const isUserTutor = user(UserQ.hasTutorRole()) ?? false
  const helpfulCount = user(UserQ.countAllHelpfuls()) ?? 0
  const superHelpfulCount = user(UserQ.countAllSuperHelpfuls()) ?? 0

  const referredUsers = user(UserQ.countReferredUsersByUserId(UserQ.id)) ?? 0
  const referredTutors = user(UserQ.countReferredTutorsByUserId(UserQ.id)) ?? 0

  const countriesImpacted = user
    .many(UserQ.getCountriesImpacted({}))
    .map(x => countryCodeToName(x`country`))
    .filter(x => x != null && x.length > 0)
    .sort()

  const countriesReachedString = countriesImpacted.join(', ')

  // N.B. We query both normal tables and UserEvents / VolunteerActivity for
  // some stats because they persist after session deletion, but our tracking
  // hasn't always been consistent. We use the maximum value.

  const pastSessionCount = Math.max(
    user(UserQ.countPastHostedSessionsIncludingMigrated({ requireLearnerAttendance: true })) ?? 0,
    user(
      UserEventQ.countPastHostedSessions({ userId: UserQ.id, requireLearnerAttendance: true })
    ) ?? 0
  )

  const countCountriesImpacted = Math.max(
    user(UserQ.countCountriesImpactedForStatistics({})) ?? 0,
    user(UserEventQ.countCountriesImpacted({ userId: UserQ.id })) ?? 0
  )

  const countLearnersImpacted = Math.max(
    user(UserQ.countLearnersImpactedForStatistics({})) ?? 0,
    user(UserEventQ.countLearnersImpacted({ userId: UserQ.id })) ?? 0
  )

  const tutoringMinutes = Math.round(user(UserQ.getTutoringMinutesWithStatsCorrection()) ?? 0)

  const progression = user(UserQ.progression) ?? PROGRESSION_DEFAULT
  const countBadges = Object.keys(user(UserQ.progression)?.achievements ?? {}).length
  const learningMinutes = Math.round(user(UserQ.getAttendedMinutes()) ?? 0)

  const totalSessionsAttended: number =
    user`COUNT(*) ${UserQ.getSessionRegistrations({
      attemptedJoining: true,
    })}` ?? 0
  const referredLearners = referredUsers - referredTutors

  const userId = user(UserQ.id)
  const isCurrentUser = currentUser.id === userId

  const learnerStats: Stat[] = [
    {
      value: progression.points ?? 0,
      label: 'SP',
      icon: faTrophy,
      color: 'yellow.solid',
    },
    {
      value: countBadges,
      label: `Badge${countBadges === 1 ? '' : 's'}`,
      icon: faCertificate,
      color: 'yellow.solid',
    },
  ]

  const tutorStats: Stat[] = [
    {
      value: countCountriesImpacted,
      label: `${countCountriesImpacted === 1 ? 'Country' : 'Countries'} Reached`,
      icon: faGlobe,
      tooltipText: countriesReachedString,
    },
    {
      value: countLearnersImpacted,
      label: `Learner${countLearnersImpacted === 1 ? '' : 's'} Impacted`,
      icon: faUsers,
    },
    {
      value: pastSessionCount,
      label: `Session${pastSessionCount === 1 ? '' : 's'} Hosted`,
      icon: faCalendar,
      infoTooltipText: isCurrentUser
        ? 'This number only includes sessions where you and at least one learner attended.'
        : undefined,
    },
    {
      value: tutoringMinutes.toLocaleString(),
      label: 'Tutoring Minutes',
      icon: faClock,
      infoTooltipText: TUTORING_MINUTES_TOOLTIP_EXPLANATION,
    },
    {
      value: helpfulCount,
      label: `Helpful${helpfulCount === 1 ? '' : 's'}`,
      icon: faThumbsUp,
    },
    {
      value: superHelpfulCount,
      label: `Super Helpful${superHelpfulCount === 1 ? '' : 's'}`,
      icon: faStar,
    },
  ]

  const moreLearnerStats: Stat[] = [
    {
      value: totalSessionsAttended,
      label: `Session${totalSessionsAttended === 1 ? '' : 's'} Attended`,
      icon: faCalendar,
    },
    {
      value: learningMinutes.toLocaleString(),
      label: 'Learning Minutes',
      icon: faClock,
    },
    {
      value: referredLearners,
      label: `Learner${referredLearners === 1 ? '' : 's'} Referred`,
      icon: faUserPlus,
    },
    {
      value: referredTutors,
      label: `Tutor${referredTutors === 1 ? '' : 's'} Referred`,
      icon: faUserPlus,
    },
  ]

  const stats = learnerStats.concat(isUserTutor ? tutorStats : []).concat(moreLearnerStats)
  const firstPartStats = stats.slice(0, SHOW_ALL_LIMIT)
  const secondPartStats = stats.slice(SHOW_ALL_LIMIT)

  return (
    <Box>
      <Heading size="xl" mb={6}>
        Stats
      </Heading>
      <ProfileItemContainer>
        <StatsList stats={firstPartStats} />
        {secondPartStats.length === 0 ? null : (
          <>
            <Collapse in={showAll}>
              <Box mt={6}>
                <StatsList stats={secondPartStats} />{' '}
              </Box>
            </Collapse>
            <Box mt={4}>
              <Button variant="ghost" onClick={() => setShowAll(x => !x)}>
                {showAll ? 'Show Less' : 'Show More'}
              </Button>
            </Box>
          </>
        )}
      </ProfileItemContainer>
    </Box>
  )
}
